import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { find } from 'lodash';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import core, { API } from '@gin-betting/core';

import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { useMemoSelector } from 'hooks';

interface Input {
    zoneId: string;
}

interface Output {
    interval: number;
    banners: BannerTypes.BannerItem[];
}

const {
    actions: { getBannersData },
    selectors: {
        selectCmsBanners,
        makeSelectBannerByZoneId,
        makeBannerZoneSettingsSelector,
    },
} = bannersModule;

const {
    utils: { getBettingOptions },
} = core;

const DEFAULT_INTERVAL = 5 * 1000;

const makeValidBannersSelector = (zoneId: string) =>
    createSelector(
        makeSelectBannerByZoneId(zoneId),
        makeBannerZoneSettingsSelector(zoneId),
        selectCmsBanners,
        (bannerZone, settings, cmsBanners) => {
            const bannerList = bannerZone?.config ?? [];

            const banners: BannerTypes.BannerItem[] = bannerList.filter(
                banner => {
                    switch (banner.type) {
                        case API.BannerType.cms_banner: {
                            return !!cmsBanners.find(b => b.id === banner.id);
                        }

                        default:
                            return true;
                    }
                }
            );

            return { banners, settings };
        }
    );

function useBannersCarousel({ zoneId }: Input): Output {
    const dispatch = useDispatch();

    const { banners, settings } = useMemoSelector(
        makeValidBannersSelector(zoneId)
    );

    const intervalPair = find(settings, { name: 'interval' });
    const interval = +(intervalPair?.value ?? DEFAULT_INTERVAL);

    useEffect(() => {
        dispatch(
            getBannersData({
                zoneId,
                marketStatuses: ACTIVE_MARKET_STATUSES,
                marketLimit: 1,
                sportIds: getBettingOptions().sportIds,
            })
        );
    }, [dispatch, zoneId]);

    return { interval, banners };
}

export default useBannersCarousel;
